import React, { Component, useEffect, useState } from 'react';
import groupBy from 'lodash/groupBy';
import { useTranslation } from 'react-i18next';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import { Header } from 'formlabs-web-components/Type';
import { StoreMenu } from '../navigation';

import { formatPrice } from '../../utils';

import AddToCart from './AddToCart';
import Amount from './Amount';
import ProductDetailHeader from './ProductDetailHeader';
import ProductOption from './ProductOption';

import { analytics } from '../../utils';

import './ProductDetailPlugin.module.css';

export class ProductConfigurator extends Component {
  constructor(props) {
    super(props);

    this.state = this.getInitialState(props);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.sku !== this.props.sku) {
      this.setState(this.getInitialState(this.props));
    }
  }

  getInitialState(props) {
    const { bundleOptions } = props;

    let availableOptions = [];
    const defaultOptions = new Map();
    if (bundleOptions) {
      const groupedOptions = groupBy(
        bundleOptions,
        option =>
          option.product_links.length > 1 ||
          (option.product_links.length === 1 && option.required === false)
      );

      // not configurable bundle options, we hide them
      if (groupedOptions['false']) {
        for (let entry of groupedOptions['false']) {
          // Handle the case where store-by-store product visibility makes some options have no items.
          if (entry.product_links.length !== 0) {
            defaultOptions.set(
              entry.option_id,
              new Map([[entry.product_links[0].sku, entry.product_links[0]]])
            );
          }
        }
      }

      // configurable bundle options
      if (groupedOptions['true']) {
        availableOptions = groupedOptions['true'];
      }
    }

    // calculate sums for all preselected options
    const optionSums = new Map();
    availableOptions.forEach(option => {
      const selectedOptions = new Map();
      const defaultItem = option.product_links.find(item => item.is_default);
      if (defaultItem) {
        selectedOptions.set(defaultItem.sku, defaultItem);
      }
      const optionSum = this.getOptionSum(selectedOptions);
      optionSums.set(option.option_id, optionSum);
    });

    const amount = 1;

    const price = this.getPrice(this.getOptionsSum(optionSums), amount);
    this.props.priceCallback(price);

    return {
      amount,
      price,
      optionSums,
      bundle: new Map(defaultOptions),
      availableOptions
    };
  }

  getOptionsSum = optionSums => {
    return Array.from(optionSums.values()).reduce(
      (sum, price) => sum + price,
      0
    );
  };

  getPrice = (optionsSum, amount) => {
    return (this.getBasePrice() + optionsSum) * amount;
  };

  updatePrice = () => {
    const price = this.getPrice(
      this.getOptionsSum(this.state.optionSums),
      this.state.amount
    );
    this.setState({
      price
    });
    this.props.priceCallback(price);
  };

  getBasePrice = () => {
    const { bundleOptions, price } = this.props;

    if (!bundleOptions) {
      return price;
    }

    return bundleOptions
      .filter(
        option =>
          option.product_links.length <= 1 &&
          (option.product_links.length === 1 && option.required === true)
      )
      .map(option => option.product_links)
      .reduce((sum, products) => {
        const price = products.reduce(
          (productSum, product) => productSum + product.price,
          0
        );
        return sum + price;
      }, 0);
  };

  handleAmountChange = amount => {
    this.props.amountCallback(amount);
    this.setState({ amount }, this.updatePrice);
  };

  getOptionSum = selectedOptions => {
    return Array.from(selectedOptions.values()).reduce(
      (sum, option) => sum + option.price,
      0
    );
  };

  handleOptionChange = (optionId, selectedOptions) => {
    const optionSum = this.getOptionSum(selectedOptions);
    const optionSums = this.state.optionSums.set(optionId, optionSum);
    const price = this.getPrice(
      this.getOptionsSum(optionSums),
      this.state.amount
    );

    this.setState(
      {
        optionSums,
        price,
        bundle: this.state.bundle.set(optionId, selectedOptions)
      },
      () => {
        this.props.bundleCallback(this.state.bundle);
        this.props.priceCallback(this.state.price);
      }
    );
  };

  priceFormatter = price =>
    formatPrice(price, this.props.language, this.props.currency);

  render() {
    const { bundleOptions, name, sku } = this.props;
    const { amount, bundle, availableOptions } = this.state;

    return (
      <>
        <div styleName="header-options">
          {bundleOptions &&
            availableOptions.map(option => (
              <div key={option.option_id}>
                <Header level={2} displayAs={2} styles={{ fontSize: '1rem' }}>
                  {option.title}
                </Header>
                <ProductOption
                  option={option}
                  onOptionChange={this.handleOptionChange}
                  priceFormatter={this.priceFormatter}
                />
              </div>
            ))}
        </div>
        <div styleName="header-actions actions">
          <div styleName="amount padded">
            <Amount onAmountChange={this.handleAmountChange} />
          </div>
          <div style={{ alignSelf: 'center' }}>
            <AddToCart
              amount={amount}
              sku={sku}
              name={name}
              bundleOptions={bundle}
            />
          </div>
        </div>
      </>
    );
  }
}

const ProductDetailPlugin = props => {
  const {
    attributes,
    baseUrl,
    bundleOptions = null,
    category,
    media,
    menu,
    name,
    sku,
    isWholesale = false,
    amount: initialAmount,
    price: initialPrice,
    showAvailability = true
  } = props;
  const [t] = useTranslation();
  const [amount, setAmount] = useState(initialAmount || 1);
  const [price, setPrice] = useState(initialPrice);
  const [activeBundleOptions, setActiveBundleOptions] = useState([]);

  useEffect(
    () => {
      analytics.track('Product Viewed', {
        sku: sku,
        category: category,
        name: name,
        brand: 'Formlabs',
        quantity: 1
      });
    },
    [sku]
  );

  const navigationEntries = [{ name: t('Store'), url: baseUrl }];
  if (category) {
    navigationEntries.push({
      name: category.name,
      url: `${baseUrl}${category.attributes.urlPath}/`
    });
  }
  if (name) {
    navigationEntries.push({ name });
  }

  return (
    <>
      <StoreMenu baseUrl={baseUrl} menu={menu} isWholesale={isWholesale} />
      <ProductDetailHeader
        actions={
          <ProductConfigurator
            {...props}
            priceCallback={p => {
              setPrice(p);
            }}
            amountCallback={a => {
              setAmount(a);
            }}
            bundleCallback={b => {
              setActiveBundleOptions(
                Array.from(b.values()).map(o => Array.from(o.values()))
              );
            }}
          />
        }
        activeBundleOptions={activeBundleOptions}
        amount={amount}
        attributes={attributes}
        baseUrl={baseUrl}
        bundleOptions={bundleOptions}
        category={category}
        media={media}
        name={name}
        price={price}
        sku={sku}
        showAvailability={showAvailability}
      />
    </>
  );
};

export default compose(
  connect(state => {
    return {
      language: state.i18n.language,
      currency: state.i18n.currency
    };
  }),
  DisplayComponent
)(ProductDetailPlugin);
